///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActiveRestorableWindow } from '../../../project-view-panel/project-windows/active-restorable-window';
import { AttachedFileDTO, BugReportDTO, SystemInfoDTO, UserBugReportDTO, UserDTO } from '../../../generated/shared-types.model';
import { Principal } from '../../auth/principal.service';
import { GtthDataUtils } from '../../../services/data-util.service';
import { GtthProjectStateService } from '../../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthFeedbackService } from '../feedback.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectSystemInfo } from '../../store/systemInfo/system-info.selectors';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'winde-report-problem-modal',
    templateUrl: './report-problem-modal.component.html',
    standalone: false
})
export class GtthReportProblemModalComponent extends ActiveRestorableWindow implements OnInit, OnDestroy {
    public isIdentified = false;
    public attachedFiles: File[];
    public reportProblem: BugReportDTO;
    public loaderKey = LoaderIds.BUG_REPORT_ID;

    public isSuccessfulAlert = false;

    private readonly systemInfo$: Observable<SystemInfoDTO> = this.store.select(selectSystemInfo);
    private version: string;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private readonly principal: Principal,
        private readonly projectStateService: GtthProjectStateService,
        private readonly feedbackService: GtthFeedbackService,
        private readonly store: Store,
        public dialogRef: MatDialogRef<GtthReportProblemModalComponent>,
        public readonly preloaderService: PreloaderService
    ) {
        super();
    }

    ngOnInit() {
        this.systemInfo$.pipe(takeUntil(this.destroy$)).subscribe((info: SystemInfoDTO) => (this.version = info.version));

        this.reportProblem = {
            title: '',
            projectId: null,
            projectName: '',
            projectCustomerName: '',
            description: '',
            attachedFiles: undefined
        } as BugReportDTO;

        this.principal.identity().then(({ login, firstName, lastName }: UserDTO) => {
            this.isIdentified = true;
            this.reportProblem.userLogin = login;
            this.reportProblem.userFullName = `${firstName} ${lastName}`;
        });

        this.attachedFiles = [];

        if (this.projectStateService.projectIsOpened()) {
            const { id, name, customerName } = this.projectStateService.getProject();
            this.reportProblem.projectId = id;
            this.reportProblem.projectName = name;
            this.reportProblem.projectCustomerName = customerName;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    closeModal() {
        this.reportProblem.title = '';
        this.reportProblem.description = '';
        this.attachedFiles = undefined;
        this.dialogRef.close('cancel');
    }

    changeFiles($event) {
        const newFiles = [];

        const fileInput: HTMLInputElement = $event.target;
        const selectedFiles: FileList = fileInput.files;

        for (let i = 0; i < selectedFiles.length; i++) {
            let isExist = false;
            const selectedFile = selectedFiles.item(i);

            for (const attachedFile of this.attachedFiles) {
                if (attachedFile.name === selectedFile.name) {
                    isExist = true;
                    break;
                }
            }

            if (!isExist) {
                newFiles.push(selectedFile);
            }
        }

        this.attachedFiles = [...this.attachedFiles, ...newFiles];
        fileInput.value = '';
    }

    removeFileFromList(file: File) {
        this.attachedFiles = this.attachedFiles.filter((attachedFile: File) => {
            return attachedFile.name !== file.name;
        });
    }

    sendReport() {
        this.preloaderService.show(this.loaderKey);

        this.reportProblem.attachedFiles = [];
        this.reportProblem.plannerVersion = this.version;

        try {
            if (!this.attachedFiles || this.attachedFiles.length === 0) {
                this.sendBugReport();
                return;
            }

            this.attachedFiles.forEach((file: File) => {
                GtthDataUtils.toBase64(file, (base64Data) => {
                    const attachedFile: AttachedFileDTO = {
                        fileName: file.name,
                        file: base64Data,
                        contentType: file.type
                    };

                    this.reportProblem.attachedFiles.push(attachedFile);

                    if (this.attachedFiles.length === this.reportProblem.attachedFiles.length) {
                        this.attachedFiles = undefined;
                        this.sendBugReport();
                    }
                });
            });
        } catch (e) {
            this.preloaderService.hide(this.loaderKey);
        }
    }

    designRequired(): boolean {
        return false;
    }

    isRestorable(): boolean {
        return this.projectStateService.projectIsOpened();
    }

    private sendBugReport(): void {
        this.feedbackService
            .getBugReportRequest(this.reportProblem)
            .pipe(
                finalize(() => {
                    this.preloaderService.hide(this.loaderKey);
                })
            )
            .subscribe((userBugReport: UserBugReportDTO) => {
                if (userBugReport.title) {
                    this.isSuccessfulAlert = true;
                }
            });
    }
}

@Component({
    selector: 'winde-report-problem-popup',
    template: '',
    standalone: false
})
export class GtthReportProblemPopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit() {
        const dialogRef = this.dialog.open(GtthReportProblemModalComponent, {
            width: '800px',
            position: {
                top: '30px'
            },
            closeOnNavigation: true
        });

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        });
    }
}
